import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { Field } from '../Field/Field';
import { ToggleableFieldProps } from './ToggleableField.types';
import { Group } from '../Group';
import { Button } from '../Button';
import { useField, useFormState } from 'react-final-form';
import { formatCurrency } from '../../util/string.util';
import { FieldPermissionFlag } from '../FieldPermissionFlag';

const ENTER = 13;

const Label = styled.label`
  font-size: 12px;
  line-height: 16px;
`;

const Value = styled.span`
  font-size: 110%;
  font-weight: bold;

  @media (max-width: 1000px) {
    font-size: 14px;
  }
`;

const Container = styled(Group)`
  margin-bottom: 7px;
  @media (max-width: 1000px) {
    margin-bottom: 17px;
  }
  button.toggle {
    transition: none;
  }
`;

function renderValue<
  FieldValue = any,
  T extends HTMLElement = HTMLElement,
  LabelKey extends string = any,
  ValueKey extends string = any,
>(
  props: ToggleableFieldProps<FieldValue, T, LabelKey, ValueKey>,
  value: FieldValue,
) {
  const {
    secure,
    type,
    optionLabelKey,
    optionValueKey,
    options,
    format,
    name,
    currency,
  } = props;

  switch (true) {
    case secure && !!value:
      return String(value).replace(/./g, '*');
    case type === 'select':
      const selectedOption = options?.find(o => o[optionValueKey] === value);
      return selectedOption?.[optionLabelKey!] ?? value;
    case currency:
      return value ? formatCurrency(+value) : '';
    case !!value:
      return format ? format(value, name) : value;
    default:
      return '-';
  }
}

export function ToggleableField<
  FieldValue = any,
  T extends HTMLElement = HTMLElement,
  LabelKey extends string = any,
  ValueKey extends string = any,
>(props: ToggleableFieldProps<FieldValue, T, LabelKey, ValueKey>): JSX.Element {
  const {
    defaultMode = 'display',
    toggleable = true,
    secure,
    ...fieldProps
  } = props;
  const [mode, setMode] = useState(defaultMode);
  const { submitFailed } = useFormState();
  const { meta } = useField(props.name);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const hasError = meta.error;

  useEffect(() => {
    // pop open field if submit failed and this field had an error
    if (submitFailed && hasError && mode === 'display') setMode('edit');
  }, [hasError, submitFailed, mode]);

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    // If Enter/Return is pressed
    // and the default mode of this field is 'display'
    // and this field is being edited
    // then set the mode back to 'display'
    if (
      event.keyCode === ENTER &&
      defaultMode === 'display' &&
      mode === 'edit'
    ) {
      // set mode back to 'display'
      setMode('display');

      // prevent form from submitting
      event.preventDefault();

      // focus on edit button once field is closed
      if (editButtonRef.current) editButtonRef.current.focus();
    }
  };

  return (
    <FieldPermissionFlag field={props.name as any}>
      {({ read, write, none }) => {
        if (none) return null as any;

        const isReadOnly = read && !write;
        const derivedMode = isReadOnly ? 'display' : mode;

        return (
          <Container
            direction="column"
            onKeyDown={e => {
              if (!isReadOnly) handleKeyDown(e);
            }}
          >
            {props.label && derivedMode === 'display' && (
              <Label>{props.label}</Label>
            )}
            <Group direction="row" space="0.5rem">
              {derivedMode === 'display' ? (
                <Field {...fieldProps}>
                  {({ input: inputProps }) => (
                    <Value tabIndex={-1}>
                      {renderValue(fieldProps, inputProps.value) as string}
                    </Value>
                  )}
                </Field>
              ) : typeof fieldProps.children === 'function' ? (
                <Field {...fieldProps}>
                  {props => {
                    // autofocus field if the default mode is 'display'
                    // so the user can start editing right away after pressing the edit button
                    (props.input as any).autoFocus = defaultMode === 'display';
                    return (fieldProps.children as any)(props);
                  }}
                </Field>
              ) : (
                <Field {...fieldProps} />
              )}
              {toggleable && write && (
                <Button
                  ref={editButtonRef}
                  size="tiny"
                  id={`edit-button-${fieldProps.name}`}
                  data-testid={`edit-button-${fieldProps.name}`}
                  tabIndex={mode === 'edit' ? -1 : undefined}
                  variant="outline"
                  border="rounded"
                  className="toggle"
                  motif={mode === 'edit' ? 'success' : 'tertiary'}
                  icon={mode === 'edit' ? 'fas fa-check' : 'fas fa-pencil-alt'}
                  onClick={() => {
                    setMode(mode === 'edit' && !hasError ? 'display' : 'edit');
                  }}
                />
              )}
            </Group>
          </Container>
        );
      }}
    </FieldPermissionFlag>
  );
}
